import React, { useState, useEffect, useRef } from 'react';
import './PLChart.css';
import { calculateComboSnapshot } from './financial';

const formatCurrency = (value) => Number(value).toFixed(2);
const formatGreek = (value) => Number(value).toFixed(4);
const TOOLTIP_WIDTH = 190;
const TOOLTIP_HEIGHT = 164;
const TOOLTIP_OFFSET = 14;

const PLChart = ({ datasets, optionRequest }) => {
  const chartRef = useRef(null);
  const [width, setWidth] = useState(0);
  const [hoveredPosition, setHoveredPosition] = useState(null);

  useEffect(() => {
    const updateWidth = () => {
      if (chartRef.current) {
        setWidth(chartRef.current.offsetWidth);
      }
    };

    const timeoutId = setTimeout(updateWidth, 100);

    const handleResize = () => {
      updateWidth();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
    };
  }, [datasets]);

  if (!datasets || datasets.length === 0) {
    return null;
  }

  const colors = ['steelblue', 'red', 'green', 'orange', 'purple'];
  const margin = { top: 0, right: 0, bottom: 0, left: 0 };
  const height = 300 - margin.top - margin.bottom;

  const allData = datasets.flatMap(d => d.pLs);
  const maxProfit = Math.max(...allData.map(d => d.profit));
  const minProfit = Math.min(...allData.map(d => d.profit));
  const minStockPrice = Math.min(...allData.map(d => d.stockPrice));
  const maxStockPrice = Math.max(...allData.map(d => d.stockPrice));
  const comboSnapshots = datasets.map((dataset, index) => ({
    title: dataset.title,
    combo: optionRequest?.optionCombos?.[index] || null,
  }));

  const getX = (price) => {
    if (maxStockPrice === minStockPrice) {
      return width / 2;
    }
    return ((price - minStockPrice) / (maxStockPrice - minStockPrice)) * width;
  };

  const getY = (profit) => {
    if (maxProfit === minProfit) {
      return height / 2;
    }
    return height - ((profit - minProfit) / (maxProfit - minProfit)) * height;
  };

  const handleMouseMove = (e) => {
    if (!width) {
      return;
    }

    const svgRect = e.currentTarget.getBoundingClientRect();
    const mouseX = e.clientX - svgRect.left;
    const mouseY = e.clientY - svgRect.top;

    let closestDatasetIndex = -1;
    let minDistance = Infinity;

    datasets.forEach((dataset, index) => {
      for (let i = 0; i < dataset.pLs.length - 1; i++) {
        const p1 = dataset.pLs[i];
        const p2 = dataset.pLs[i + 1];
        const x1 = getX(p1.stockPrice);
        const y1 = getY(p1.profit);
        const x2 = getX(p2.stockPrice);
        const y2 = getY(p2.profit);

        const dx = x2 - x1;
        const dy = y2 - y1;
        const l2 = dx * dx + dy * dy;

        if (l2 === 0) continue;

        let t = ((mouseX - x1) * dx + (mouseY - y1) * dy) / l2;
        t = Math.max(0, Math.min(1, t));

        const closestX = x1 + t * dx;
        const closestY = y1 + t * dy;

        const distance = Math.sqrt(Math.pow(mouseX - closestX, 2) + Math.pow(mouseY - closestY, 2));

        if (distance < minDistance) {
          minDistance = distance;
          closestDatasetIndex = index;
        }
      }
    });

    if (closestDatasetIndex !== -1) {
      const closestDataset = datasets[closestDatasetIndex];
      const stockPrices = closestDataset.pLs.map(p => p.stockPrice);
      const profits = closestDataset.pLs.map(p => p.profit);
      const clampedMouseX = Math.max(0, Math.min(width, mouseX));
      const mouseStockPrice = minStockPrice + (clampedMouseX / width) * (maxStockPrice - minStockPrice);

      let i = 0;
      for (i = 0; i < stockPrices.length - 1; i++) {
        if (mouseStockPrice >= stockPrices[i] && mouseStockPrice <= stockPrices[i + 1]) {
          break;
        }
      }

      const x1 = stockPrices[i];
      const y1 = profits[i];
      const x2 = stockPrices[i + 1];
      const y2 = profits[i + 1];

      if (x1 !== undefined && y1 !== undefined && x2 !== undefined && y2 !== undefined) {
        const interpolatedY = x2 === x1 ? y1 : y1 + ((y2 - y1) * (mouseStockPrice - x1)) / (x2 - x1);
        const comboSnapshot = comboSnapshots[closestDatasetIndex]?.combo
          ? calculateComboSnapshot(optionRequest, comboSnapshots[closestDatasetIndex].combo, mouseStockPrice)
          : null;
        const tooltipLeft = Math.max(
          8,
          Math.min(width - TOOLTIP_WIDTH - 8, clampedMouseX + TOOLTIP_OFFSET)
        );
        const tooltipTop = Math.max(
          8,
          Math.min(height - TOOLTIP_HEIGHT - 8, getY(comboSnapshot?.profit ?? interpolatedY) - TOOLTIP_HEIGHT / 2)
        );

        setHoveredPosition({
          x: clampedMouseX,
          y: getY(comboSnapshot?.profit ?? interpolatedY),
          tooltipLeft,
          tooltipTop,
          stockPrice: mouseStockPrice,
          profit: comboSnapshot?.profit ?? interpolatedY,
          delta: comboSnapshot?.delta ?? null,
          gamma: comboSnapshot?.gamma ?? null,
          theta: comboSnapshot?.theta ?? null,
          vega: comboSnapshot?.vega ?? null,
          datasetIndex: closestDatasetIndex,
          title: closestDataset.title,
        });
      }
    }
  };

  const handleMouseLeave = () => {
    setHoveredPosition(null);
  };

  const stockPriceLabels = Array.from({ length: 11 }, (_, i) => {
    const price = minStockPrice + (i / 10) * (maxStockPrice - minStockPrice);
    return price.toFixed(2);
  });

  const profitLabels = Array.from({ length: 11 }, (_, i) => {
    const profit = minProfit + (i / 10) * (maxProfit - minProfit);
    return profit.toFixed(2);
  });

  return (
    <div className="pl-chart-container">
      <h3 className="text-xl font-bold text-center my-4">Profit/Loss Chart</h3>
      <div className="chart-with-labels">
        <div className="chart-box" ref={chartRef}>
          <svg width={width} height={height + margin.top + margin.bottom} onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave}>
            <g transform={`translate(${margin.left}, ${margin.top})`}>
              {/* Grid lines */}
              {stockPriceLabels.map((price, i) => (
                <line
                  key={`v-line-${i}`}
                  x1={getX(price)}
                  y1={0}
                  x2={getX(price)}
                  y2={height}
                  stroke="lightgray"
                  strokeDasharray="2,2"
                />
              ))}
              {profitLabels.map((profit, i) => (
                <line
                  key={`h-line-${i}`}
                  x1={0}
                  y1={getY(profit)}
                  x2={width}
                  y2={getY(profit)}
                  stroke="lightgray"
                  strokeDasharray="2,2"
                />
              ))}
              {datasets.map((dataset, i) => (
                <polyline
                  key={i}
                  fill="none"
                  stroke={colors[i % colors.length]}
                  strokeWidth="2"
                  points={dataset.pLs.map(d => `${getX(d.stockPrice)},${getY(d.profit)}`).join(' ')}
                />
              ))}
              {hoveredPosition && (
                <circle
                  cx={hoveredPosition.x}
                  cy={hoveredPosition.y}
                  r={5}
                  fill="none"
                  stroke={colors[hoveredPosition.datasetIndex % colors.length]}
                  strokeWidth={2}
                />
              )}
            </g>
          </svg>
          {hoveredPosition && (
            <div
              className="chart-tooltip chart-tooltip-overlay"
              style={{ left: hoveredPosition.tooltipLeft, top: hoveredPosition.tooltipTop }}
            >
              <div className="chart-tooltip-title">{hoveredPosition.title}</div>
              <div className="chart-tooltip-grid">
                <span>Stock</span>
                <span>{formatCurrency(hoveredPosition.stockPrice)}</span>
                <span>P/L</span>
                <span>{formatCurrency(hoveredPosition.profit)}</span>
                <span>Delta</span>
                <span>{hoveredPosition.delta === null ? 'N/A' : formatGreek(hoveredPosition.delta)}</span>
                <span>Gamma</span>
                <span>{hoveredPosition.gamma === null ? 'N/A' : formatGreek(hoveredPosition.gamma)}</span>
                <span>Theta</span>
                <span>{hoveredPosition.theta === null ? 'N/A' : formatGreek(hoveredPosition.theta)}</span>
                <span>Vega</span>
                <span>{hoveredPosition.vega === null ? 'N/A' : formatGreek(hoveredPosition.vega)}</span>
              </div>
            </div>
          )}
        </div>
        <div className="profit-labels">
          {profitLabels.slice().reverse().map((label, i) => (
            <span key={i} className="profit-label">{label}</span>
          ))}
        </div>
      </div>
      <div className="stock-price-labels">
        {stockPriceLabels.map((label, i) => (
          <span key={i} className="stock-price-label">{label}</span>
        ))}
      </div>
      <div className="pl-chart-legend">
        {datasets.map((dataset, i) => (
          <div key={i} className="legend-item">
            <span className="legend-color" style={{ backgroundColor: colors[i % colors.length] }}></span>
            <span>{dataset.title}</span>
          </div>
        ))}
      </div>
      <p className="warning-message">This chart is for illustrative purposes only and does not reflect actual results.</p>
    </div>
  );
};

export default PLChart;
